import React, { FC, useRef, useState } from 'react';
import { QueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import queryString from 'query-string';
import { useRouter } from 'next/router';

import { EmptyResults } from '@agendapro/emerald-icons';
import { getCompanyOverview } from '@/services/company/company.repo';
import { GlobalContentLayout } from '@/components';
import { PageProvider } from '@/context/PageContext';
import { Paragraph, ViewMore, SearchBar, Skeleton } from '@/UI';
import { validateIfTextContainsStr } from '@/UI/Utils';
import useCheckRefScrolled from '@/hooks/useCheckRefScrolled';
import { LocationsProps } from './types';
import { LocationsResponse, useLocations } from '@/services/locations';
import { CardLocalItem } from './components/CardLocalItem';
import CardLocalItemSkeleton from './components/CardLocalItemSkeleton';

import * as St from './Locations.styles';

const ActivationBar = dynamic(() => import('@/components/ActivationBar/ActivationBar'));

const Locations: FC<LocationsProps> = ({ companyOverview, companySlug, queryParams }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const searchRef = useRef(null);
  const [search, setSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<LocationsResponse | undefined>();
  const [filteredLocations, setFilteredLocations] = useState<{
    data: LocationsResponse[];
    dataFiltered: LocationsResponse[];
  }>({
    data: [],
    dataFiltered: [],
  });

  const { data, isLoading } = useLocations({
    companyId: companyOverview?.id,
    onSuccess: (data) => {
      const validLocations = data?.filter((location) => location.validLocation);

      if (validLocations.length === 1) {
        const countryCode = window.location.pathname.split('/')[1];
        const redirectUrl = `/${countryCode}/sucursal/${validLocations[0].id}/${queryParams || ''}`;

        router.push(redirectUrl);
        return;
      }

      setFilteredLocations({ data: validLocations, dataFiltered: validLocations });
      setSelectedLocation(validLocations[0]);
    },
  });

  const locations = data?.filter((location) => location.validLocation);
  const scrolled = useCheckRefScrolled(isLoading ? null : searchRef);

  const onSearch = (value) => {
    setSearch(value);
    const filterItems = ['locationName', 'address'];
    const filtered = filteredLocations.data.filter(
      (listToMatchText) =>
        validateIfTextContainsStr(listToMatchText[filterItems[0]], value) ||
        validateIfTextContainsStr(listToMatchText[filterItems[1]], value),
    );

    setFilteredLocations({
      data: locations || [],
      dataFiltered: filtered,
    });
  };
  const getFiltering = (isRef: boolean) => (
    <St.SearchBarContainer ref={isRef ? searchRef : null} scrolled={!!scrolled} isRef={isRef}>
      <SearchBar
        className="locationsSearchBar"
        value={search}
        onChange={onSearch}
        placeholder={`${t('FIND_BRANCH_OFFICE')}`}
        totalLocations={filteredLocations.data.length}
        totalFilteredLocations={filteredLocations.dataFiltered.length}
      />
    </St.SearchBarContainer>
  );

  return (
    <>
      <PageProvider
        companySlug={companySlug}
        companyOverview={companyOverview}
        nextSeo={{
          title: `${companyOverview.name} | ${t('ONLINE_BOOKING')}`,
          description: `${t('SEO.LOCATIONS_DESCRIPTION_FIRST')} ${companyOverview.name} ${t(
            'SEO.LOCATIONS_DESCRIPTION_SECOND',
          )}`,
        }}
        queryParams={queryParams}
      >
        <St.Container hasDescription={!!companyOverview?.description}>
          <GlobalContentLayout>
            {!companyOverview?.minisiteV2Active && <ActivationBar />}
            <St.CardsAndMapWrapper>
              {companyOverview?.description && (
                <St.CompanyDescriptionWrapper>
                  <ViewMore textContent={companyOverview.description} maxNumOfLines={4} />
                </St.CompanyDescriptionWrapper>
              )}
              <Paragraph className="sectionTitle">{t('CHOOSE_A_LOCATION')}</Paragraph>
              <St.HeaderScrollBox>
                {isLoading ? (
                  <Skeleton style={{ height: 36, width: '100%', borderRadius: 4 }} />
                ) : (
                  <>
                    {getFiltering(true)}
                    {getFiltering(false)}
                  </>
                )}
              </St.HeaderScrollBox>
              {!!filteredLocations.data.length && !filteredLocations.dataFiltered.length ? (
                <St.NoResultContainer>
                  <EmptyResults size={120} className="emptyResultsIcon" />
                  <Paragraph className="noResultTitle">{t('WE_ARE_SORRY')}</Paragraph>
                  <Paragraph className="noResultSubtitle">{t('NO_LOCATION_FOUND')}</Paragraph>
                  <button onClick={() => onSearch('')} type="button" className="seeAllLocationsButton">
                    {t('SEE_ALL_LOCATIONS')}
                  </button>
                </St.NoResultContainer>
              ) : (
                <St.CardLocalWrapper>
                  <St.CardsWrapper>
                    {isLoading ? (
                      <>
                        {[...Array(4).keys()].map((el) => (
                          <CardLocalItemSkeleton key={el} />
                        ))}
                      </>
                    ) : (
                      <>
                        {filteredLocations.dataFiltered.map((location) => (
                          <CardLocalItem
                            key={location.id}
                            setSelectedLocation={setSelectedLocation}
                            isSelected={!!(location.id === selectedLocation?.id)}
                            location={location}
                            queryParams={queryParams}
                          />
                        ))}
                      </>
                    )}
                  </St.CardsWrapper>
                </St.CardLocalWrapper>
              )}
            </St.CardsAndMapWrapper>
          </GlobalContentLayout>
        </St.Container>
      </PageProvider>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const companySlug = ctx.req.headers.host?.split('.')[0];
  const { countryCode, ...params } = ctx.query as { countryCode: string };
  const queryClient = new QueryClient();

  try {
    const companyOverViewData = await queryClient.fetchQuery(
      ['getCompanyOverview', companySlug, countryCode],
      ({ queryKey }) => getCompanyOverview(queryKey[1], queryKey[2]),
    );

    const query = queryString.stringify(params);
    const queryParams = query ? `?${query}` : '';

    return {
      props: {
        companyOverview: companyOverViewData,
        companySlug,
        queryParams,
      },
    };
  } catch (e) {
    return {
      redirect: {
        permanent: false,
        destination: 'https://agendapro.com/',
      },
    };
  }
};

export default Locations;
